import {
  FaSearch,
  FaDatabase,
  FaUserPlus,
  FaFilter,
  FaRedo,
} from "react-icons/fa";
import { Form, Button, Spinner } from "react-bootstrap";
import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import axios from "../../axios";
import { FiChevronDown, FiX } from "react-icons/fi";
import { Checkbox } from "primereact/checkbox";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";

type FormControlElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement;

interface SearchFilterProps {
  queryField: string;
  searchString: string;
  handleQueryFieldChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSearchStringChange: (e: React.ChangeEvent<FormControlElement>) => void;
  fetchResults: (params: {
    field: string;
    value: string | string[];
  }) => Promise<void>;
  handleDealForm: () => void;
  selectedOwners: string[];
  handleOwnerCheckboxChange: (owner: string) => void;
  ownerOptions: string[];
  resetFilters: () => void;
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  queryField,
  searchString,
  handleQueryFieldChange,
  handleSearchStringChange,
  fetchResults,
  handleDealForm,
  selectedOwners,
  handleOwnerCheckboxChange,
  ownerOptions,
  resetFilters,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [owners, setOwners] = useState<
    { firstName: string; LastName: string }[]
  >([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (queryField === "owner") {
      axios
        .get("/api/owners")
        .then((res) => {
          setOwners(Array.isArray(res.data) ? res.data : []);
        })
        .catch((error) => {
          console.error("Failed to fetch owners:", error);
          toast.error("Failed to load owners.");
        });
    }
  }, [queryField]);

  const handleQueryClick = async () => {
    if (!queryField) {
      toast.warning("Select a field to search.");
      return;
    }

    const isEmptyString = (val: unknown): boolean =>
      typeof val === "string" && val.trim().length === 0;

    if (
      (queryField === "dealNo" && isEmptyString(searchString)) ||
      (queryField === "owner" && selectedOwners.length === 0) ||
      ((queryField === "dealstatus" || queryField === "dealStage") &&
        searchString === "select field")
    ) {
      toast.warning("Enter a value to search.");
      return;
    }

    setIsLoading(true);
    setTimeout(async () => {
      try {
        const valueToSend =
          queryField === "owner" ? selectedOwners : searchString;
        await fetchResults({ field: queryField, value: valueToSend });
      } catch (error) {
        toast.error("Failed to fetch results.");
      }
      setIsLoading(false);
    }, 5000);
  };

  return (
    <Form autoComplete="off" className="mt-0 pt-3 pb-3">
      <div className="flex flex-wrap items-center justify-between w-full">
        <div className="flex gap-3">
          <div className="min-w-[250px]">
            <Form.Group controlId="queryField" className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" />
              <Form.Select
                value={queryField}
                onChange={handleQueryFieldChange}
                className="border rounded-md p-2 w-full h-12 focus:ring-2 focus:ring-blue-500 px-5"
              >
                <option value="all">All</option>
                <option value="dealNo">Deal No</option>
                <option value="owner">Owner</option>
                <option value="dealstatus">Deal Status</option>
                <option value="dealStage">Deal Stage</option>
              </Form.Select>
            </Form.Group>
          </div>

          {queryField === "dealNo" && (
            <div className="min-w-[300px]">
              <Form.Group controlId="searchString" className="relative">
                <Form.Control
                  type="text"
                  value={searchString}
                  onChange={handleSearchStringChange}
                  placeholder="Enter Deal Number"
                  className="border rounded-md p-2 w-full h-12 focus:ring-2 focus:ring-blue-500 px-2"
                />
                <FaSearch className="absolute right-5 top-4 text-gray-500" />
              </Form.Group>
            </div>
          )}

          {queryField === "owner" && (
            <div ref={dropdownRef} className="min-w-[300px] relative">
              <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="relative border border-gray-300 rounded-lg px-4 py-2 cursor-pointer bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex flex-wrap items-start gap-2 max-h-[90px] overflow-y-auto pr-8"
                style={{ minHeight: "48px" }}
              >
                {selectedOwners.length > 0 ? (
                  selectedOwners.map((owner, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full flex items-center"
                    >
                      {owner}
                      <Button
                        type="button"
                        className="ml-1 p-0 hover:text-red-600 focus:outline-none"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOwnerCheckboxChange(owner);
                        }}
                      >
                        <FiX className="w-4 h-4" />
                      </Button>
                    </span>
                  ))
                ) : (
                  <span className="text-gray-500 mt-1">Select owners</span>
                )}
                <FiChevronDown className="absolute right-3 bottom-2 top-4 text-gray-600 pointer-events-none" />
              </div>
              {isDropdownOpen && (
                <div className="absolute mt-1 border border-gray-300 rounded-lg bg-white shadow-md max-h-52 overflow-auto z-20 w-full">
                  {owners.length > 0 ? (
                    owners.map((owner) => {
                      const fullName = `${owner.firstName} ${owner.LastName}`;
                      return (
                        <div
                          key={fullName}
                          className="flex items-center px-3 py-2 hover:bg-gray-100"
                        >
                          <Checkbox
                            inputId={`owner-${fullName}`}
                            checked={selectedOwners.includes(fullName)}
                            onChange={() => handleOwnerCheckboxChange(fullName)}
                            className="p-checkbox-sm border-gray-500"
                          />
                          <label
                            htmlFor={`owner-${fullName}`}
                            className="ml-2 text-sm cursor-pointer"
                          >
                            {fullName}
                          </label>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 text-sm px-3 py-2">
                      No owners available
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {(queryField === "dealstatus" || queryField === "dealStage") && (
            <div className="min-w-[200px]">
              <Form.Group controlId="searchString">
                <Form.Select
                  value={searchString}
                  onChange={handleSearchStringChange}
                  className="border rounded-md p-2 h-12 focus:ring-2 focus:ring-blue-500 px-4 cursor-pointer"
                >
                  {queryField === "dealstatus" && (
                    <>
                      <option value="In-progress">In-progress</option>
                      <option value="Active">Active</option>
                    </>
                  )}
                  {queryField === "dealStage" && (
                    <>
                      <option value="L0">L0</option>
                      <option value="L1">L1</option>
                      <option value="L2">L2</option>
                      <option value="L3">L3</option>
                    </>
                  )}
                </Form.Select>
              </Form.Group>
            </div>
          )}
          <Button
            variant="success"
            onClick={handleQueryClick}
            className="px-4 h-12 !font-semibold whitespace-nowrap flex items-center justify-center"
            disabled={isLoading}
            style={{ minWidth: "150px" }}
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <FaDatabase className="inline-block mr-2" /> Query
              </>
            )}
          </Button>

          <Button
            variant="outline-primary"
            onClick={async () => {
              setIsDropdownOpen(false);
              setIsResetting(true);
              setTimeout(async () => {
                await resetFilters();
                setIsResetting(false);
              }, 5000);
            }}
            className="px-4 h-12 !font-semibold whitespace-nowrap flex items-center justify-center"
            disabled={isResetting}
            style={{ minWidth: "150px" }}
          >
            {isResetting ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <FaRedo className="inline-block mr-2" /> Reset
              </>
            )}
          </Button>
        </div>
        <div>
          <Button
            variant="primary"
            onClick={handleDealForm}
            className="px-4 h-12 !font-semibold flex items-center whitespace-nowrap"
          >
            <FaUserPlus className="inline-block mb-1 mr-2" /> Add Deal Form
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default SearchFilter;
